import * as z from 'zod/v4'

import {CommandCallerSchema} from './caller'

// Bind a namespace once, get back a typed action creator for that namespace.
// The returned createAction is the only way to build a branch; it bakes in the
// canonical shape ({command: `${namespace}/${action}` literal, caller?, params?}) with
// z.strictObject so unknown keys are rejected. The three overloads carry the
// precise per-branch types (no-params / required / optional); the implementation
// builds the runtime shape inline so there is no separate widened helper to drift from.
// caller lives on every branch here (rather than being extended on in index.ts) because
// .extend over the branch union degrades the inferred Command type; commandToolDefinitions
// strips it so the LLM never sees it. Correlation of a dispatched command to its response
// is carried by the response's toolCallId (see CommandResponseSchema), so branches
// intentionally have no id field.
export function createNamespacedActionCreator<N extends string>(namespace: N) {
  function createAction<A extends string>(
    action: A
  ): z.ZodObject<{command: z.ZodLiteral<`${N}_${A}`>; caller: z.ZodCatch<z.ZodOptional<typeof CommandCallerSchema>>}>
  function createAction<A extends string, P extends z.ZodObject<z.ZodRawShape>>(
    action: A,
    params: P
  ): z.ZodObject<{
    command: z.ZodLiteral<`${N}_${A}`>
    caller: z.ZodCatch<z.ZodOptional<typeof CommandCallerSchema>>
    params: P
  }>
  function createAction<A extends string, P extends z.ZodObject<z.ZodRawShape>>(
    action: A,
    params: z.ZodOptional<P>
  ): z.ZodObject<{
    command: z.ZodLiteral<`${N}_${A}`>
    caller: z.ZodCatch<z.ZodOptional<typeof CommandCallerSchema>>
    params: z.ZodOptional<P>
  }>
  function createAction(action: string, params?: z.ZodType) {
    return z.strictObject({
      command: z.literal(`${namespace}_${action}`),
      // .catch drops an unrecognized caller instead of failing the parse: caller is
      // analytics-only metadata, so a version-skewed value costs the attribution,
      // never the command. Absent from the emitted JSON Schema (parse-time behavior).
      caller: CommandCallerSchema.optional().catch(undefined),
      ...(params ? {params} : {})
    })
  }
  return createAction
}

// The exhaustive union of `command` literals carried by a tuple of action branches.
// Lets a namespace's descriptions map be checked for completeness against its actions
// via `satisfies Record<CommandValueFor<typeof xActions>, string>`.
export type CommandValueFor<Actions extends readonly z.ZodType[]> =
  z.infer<Actions[number]> extends {command: infer C extends string} ? C : never
